import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Clock3,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { CopyIcon, XIcon } from "../../components/icons";
import { useTranslation } from "react-i18next";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import Toast from "../Toast/Toast";
import { formatDate } from "../../utils/formatDate";
import {
  STATUS_TONE_STYLES,
  getCredentialStatusTone,
  getVerificationCheckStatus,
  getVerificationCheckTone,
  inferCredentialStatus,
  normalizeCredentialStatus,
} from "../../utils/credentialStatusUtils";

const DETAILS_LAYOUT = [
  { label: "history:issuer", key: "issuer_id" },
  { label: "history:keyId", key: "key_id" },
  { label: "history:algorithm", key: "algorithm" },
  { label: "history:issued", key: "issued_at", isDate: true },
  { label: "history:expiry", path: ["expiry", "expired_at"], isDate: true },
  {
    label: "history:revocationDate",
    path: ["revocation", "revoked_at"],
    isDate: true,
  },
  { label: "history:subject", key: "subject_id" },
];

const getValueAtPath = (source, path) =>
  path.reduce((acc, part) => acc?.[part], source);

const formatDisplayDate = (value, t) => {
  const formatted = formatDate(value);
  return formatted === "N/A"
    ? t("history:unavailable", { defaultValue: "Unavailable" })
    : formatted;
};

const truncateMiddle = (value, t, start = 20, end = 14) => {
  if (!value || value.length <= start + end + 3)
    return value || t("history:unavailable", { defaultValue: "Unavailable" });
  return `${value.slice(0, start)}...${value.slice(-end)}`;
};

const toSentenceCase = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const BLOCKCHAIN_DISPLAY_FIELDS = [
  { key: "issuer_did", labelKey: "blockchainIssuerDid", mono: true },
  { key: "vc_anchored_at", labelKey: "blockchainVcAnchoredAt" },
  {
    key: "credential_hash_full",
    labelKey: "blockchainCredentialHash",
    mono: true,
    truncate: true,
  },
  { key: "Network", labelKey: "blockchainNetwork", altKey: "network" },
];

const getBlockchainDetailFields = (blockchain) => {
  if (!blockchain) return [];

  return BLOCKCHAIN_DISPLAY_FIELDS.flatMap((field) => {
    const value = blockchain[field.key] ?? blockchain[field.altKey];
    if (value == null || String(value).trim() === "") return [];

    return [{ ...field, value: String(value).trim() }];
  });
};

const getOverallIcon = (statusKey) => {
  if (statusKey === "revoked") return ShieldAlert;
  if (statusKey === "expired") return Clock3;
  if (statusKey === "not_verified") return AlertCircle;
  return ShieldCheck;
};

const getCheckIcon = (section, tone, rawStatus) => {
  const normalizedStatus = String(rawStatus || "").toLowerCase();

  if (section === "revocation" && normalizedStatus.includes("revoked")) {
    return Ban;
  }

  if (section === "expiry" && normalizedStatus.includes("expired")) {
    return XCircle;
  }

  if (tone === "danger") return XCircle;
  if (tone === "warning") return AlertCircle;
  return CheckCircle2;
};

const VerificationCheckCard = ({
  section,
  title,
  subtitle,
  status,
  forceNeutral = false,
  t,
}) => {
  const tone = forceNeutral
    ? "neutral"
    : getVerificationCheckTone(section, status);
  const palette = STATUS_TONE_STYLES[tone];
  const Icon = getCheckIcon(section, tone, status);

  return (
    <div
      className="rounded-xl border-none px-3 py-3 sm:px-4 sm:py-3.5"
      style={{
        borderColor: palette.border,
        backgroundColor: palette.surfaceMuted,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: palette.surface,
            color: palette.text,
          }}
        >
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <p className="text-sm font-semibold leading-6 sm:text-base">
              {title ||
                t("history:unavailable", { defaultValue: "Unavailable" })}
            </p>
            <span
              className="inline-flex w-fit flex-shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold"
              style={{
                backgroundColor: palette.surface,
                borderColor: palette.border,
                color: palette.text,
              }}
            >
              {toSentenceCase(
                status ||
                  t("history:unavailable", { defaultValue: "Unavailable" }),
              )}
            </span>
          </div>
          {subtitle ? (
            <p className="mt-1 break-all text-xs leading-5 text-gray-500 sm:break-words sm:text-sm sm:text-gray-600">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const IssuerIdentityCard = ({
  issuerDid,
  issuerResolvedStatus,
  anchoredStatus,
  showAnchoredPill = false,
  forceNeutral = false,
  t,
}) => {
  const issuerTone = forceNeutral
    ? "neutral"
    : getVerificationCheckTone("issuer", issuerResolvedStatus);
  const anchoredTone =
    showAnchoredPill && anchoredStatus
      ? forceNeutral
        ? "neutral"
        : getVerificationCheckTone("blockchain_issuer", anchoredStatus)
      : "neutral";

  const issuerPalette = STATUS_TONE_STYLES[issuerTone];
  const anchoredPalette = STATUS_TONE_STYLES[anchoredTone];
  const IssuerIcon = getCheckIcon("issuer", issuerTone, issuerResolvedStatus);
  const AnchoredIcon = getCheckIcon(
    "blockchain_issuer",
    anchoredTone,
    anchoredStatus,
  );
  const issuerStatusLabel = toSentenceCase(
    issuerResolvedStatus ||
      t("history:unavailable", { defaultValue: "Unavailable" }),
  );
  const anchoredStatusKey = getVerificationCheckStatus(
    "blockchain_issuer",
    anchoredStatus,
  );
  const anchoredStatusLabel =
    anchoredStatusKey === "anchored"
      ? t("history:anchoredOnBlockchain", {
          defaultValue: "Anchored on blockchain",
        })
      : anchoredStatusKey === "not_anchored"
        ? t("history:notAnchoredOnBlockchain", {
            defaultValue: "Not anchored on blockchain",
          })
        : toSentenceCase(
            anchoredStatus ||
              t("history:unavailable", { defaultValue: "Unavailable" }),
          );

  return (
    <div
      className="rounded-xl border-none px-3 py-3 sm:px-4 sm:py-3.5"
      style={{
        borderColor: issuerPalette.border,
        backgroundColor: issuerPalette.surfaceMuted,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: issuerPalette.surface,
            color: issuerPalette.text,
          }}
        >
          <IssuerIcon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <p className="text-sm font-semibold leading-6 sm:text-base">
              {t("history:issuerDid", { defaultValue: "Issuer DID" })}
            </p>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <span
                className="inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold"
                style={{
                  backgroundColor: issuerPalette.surface,
                  borderColor: issuerPalette.border,
                  color: issuerPalette.text,
                }}
              >
                <IssuerIcon className="h-3.5 w-3.5" />
                {issuerStatusLabel}
              </span>

              {showAnchoredPill ? (
                <span
                  className="inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold"
                  style={{
                    backgroundColor: anchoredPalette.surface,
                    borderColor: anchoredPalette.border,
                    color: anchoredPalette.text,
                  }}
                >
                  <AnchoredIcon className="h-3.5 w-3.5" />
                  {anchoredStatusLabel}
                </span>
              ) : null}
            </div>
          </div>

          {issuerDid ? (
            <p className="mt-1 break-all text-xs leading-5 text-gray-500 sm:break-words sm:text-sm sm:text-gray-600">
              {issuerDid}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const BlockchainVerificationCard = ({
  blockchain,
  forceNeutral = false,
  t,
}) => {
  const status = blockchain?.status;
  const tone = forceNeutral
    ? "neutral"
    : getVerificationCheckTone("blockchain_vc", status);
  const palette = STATUS_TONE_STYLES[tone];
  const Icon = getCheckIcon("blockchain_vc", tone, status);
  const message = blockchain?.message?.toString().trim();
  const messageLines = message
    ? message
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    : [];
  const detailFields = getBlockchainDetailFields(blockchain);

  const fieldLabelDefaults = {
    blockchainIssuerDid: "Issuer DID",
    blockchainVcAnchoredAt: "VC anchored at",
    blockchainCredentialHash: "Credential hash",
    blockchainNetwork: "Network",
  };

  const getFieldLabel = (field) =>
    t(`history:${field.labelKey}`, {
      defaultValue: fieldLabelDefaults[field.labelKey] || field.labelKey,
    });

  const handleCopyBlockchainDetails = async () => {
    const text = detailFields
      .map((field) => `${getFieldLabel(field)}: ${field.value}`)
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
      Toast({
        type: "success",
        title: t("history:success", { defaultValue: "Success" }),
        message: t("history:blockchainDetailsCopied", {
          defaultValue: "Blockchain details copied to clipboard",
        }),
      });
    } catch {
      Toast({
        type: "error",
        title: t("history:error", { defaultValue: "Error" }),
        message: t("history:failed_to_copy_link", {
          defaultValue: "Failed to copy link",
        }),
      });
    }
  };

  return (
    <div
      className="rounded-xl border-none px-3 py-3 sm:px-4 sm:py-3.5"
      style={{
        borderColor: palette.border,
        backgroundColor: palette.surfaceMuted,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: palette.surface,
            color: palette.text,
          }}
        >
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <p className="text-sm font-semibold leading-6 sm:text-base">
              {t("history:blockchainVerification", {
                defaultValue: "Blockchain verification",
              })}
            </p>
            <span
              className="inline-flex w-fit flex-shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold"
              style={{
                backgroundColor: palette.surface,
                borderColor: palette.border,
                color: palette.text,
              }}
            >
              {toSentenceCase(
                status ||
                  t("history:unavailable", { defaultValue: "Unavailable" }),
              )}
            </span>
          </div>

          {messageLines.length > 0 ? (
            <div className="text-xs text-gray-500 sm:text-sm sm:text-gray-600 mt-2">
              {message}
            </div>
          ) : null}

          {detailFields.length > 0 ? (
            <div
              className="rounded-lg border px-3 py-3 sm:px-4 sm:py-3.5"
              style={{
                borderColor: palette.border,
                backgroundColor: palette.surface,
              }}
            >
              <div className="mb-3 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCopyBlockchainDetails}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2"
                  style={{
                    borderColor: palette.border,
                    backgroundColor: palette.surfaceMuted,
                    color: palette.text,
                  }}
                  aria-label={t("history:copyBlockchainDetails", {
                    defaultValue: "Copy blockchain details",
                  })}
                >
                  <CopyIcon size={14} className="inline-flex" aria-hidden />
                  {t("history:copyDetails", { defaultValue: "Copy" })}
                </button>
              </div>

              <div className="grid gap-x-6 gap-y-3 md:grid-cols-[140px_1fr]">
                {detailFields.map((field) => (
                  <Fragment key={field.key}>
                    <div className="flex items-center text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
                      {getFieldLabel(field)}
                    </div>
                    <div
                      className={`break-all text-right text-sm text-[#111827] sm:min-h-[1.25rem] sm:flex sm:items-center sm:justify-end ${
                        field.mono ? "font-mono text-xs sm:text-sm" : ""
                      }`}
                      title={field.value}
                    >
                      {field.truncate
                        ? truncateMiddle(field.value, t, 18, 18)
                        : field.value}
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ShimmerBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-gray-200/80 ${className}`} />
);

const StepSkeletonCard = () => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3.5">
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <div className="h-5 w-36 animate-pulse rounded bg-gray-200" />
        <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200" />
      </div>
    </div>
  </div>
);

const StepLoadingCard = () => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5">
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 flex-shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <div className="h-5 w-36 animate-pulse rounded bg-gray-200" />
        <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200" />
      </div>
    </div>
  </div>
);

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="flex flex-col items-center text-center">
      <ShimmerBlock className="h-14 w-14 rounded-full" />
      <ShimmerBlock className="mt-4 h-8 w-48 rounded-full" />
      <ShimmerBlock className="mt-4 h-10 w-full max-w-xl rounded-2xl" />
    </div>

    <div className="grid gap-3">
      {[0, 1, 2, 3].map((item) => (
        <div
          key={item}
          className="rounded-xl border border-[#bbf7d0] bg-[#ecfdf3] px-4 py-4"
        >
          <div className="flex items-start gap-3">
            <ShimmerBlock className="h-8 w-8 rounded-full" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <ShimmerBlock className="h-6 w-40" />
                  <ShimmerBlock className="mt-2 h-4 w-32" />
                </div>
                <ShimmerBlock className="h-7 w-20 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="rounded-2xl border-l-4 border-[#16a34a] bg-[#ecfdf3] px-4 py-4">
      <ShimmerBlock className="h-8 w-64" />
      <ShimmerBlock className="mt-3 h-4 w-full" />
      <ShimmerBlock className="mt-2 h-4 w-11/12" />
    </div>

    <div className="border-t border-gray-200 pt-4">
      <div className="grid gap-x-6 gap-y-3 md:grid-cols-[140px_1fr]">
        {[0, 1, 2, 3].map((item) => (
          <Fragment key={item}>
            <ShimmerBlock className="h-4 w-20" />
            <ShimmerBlock className="ml-auto h-4 w-40" />
          </Fragment>
        ))}
      </div>
    </div>
  </div>
);

const STEP_LOAD_DURATION = 650;
const STEP_INTERVAL = 900;

export default function CredentialStatusDetailsModal({
  isOpen,
  onClose,
  encodedData,
  fallbackStatus,
  onResolved,
}) {
  const { t } = useTranslation();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stepStates, setStepStates] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const resolveTimerRef = useRef(null);

  const blockchain = details?.blockchain;
  const isLegacyBlockchainApi =
    blockchain != null &&
    Object.prototype.hasOwnProperty.call(blockchain, "enabled");
  const blockchainEnabled = blockchain?.enabled === true;
  const issuerOnChain = blockchain?.issuer_on_chain === true;
  const showLegacyCombinedUi =
    isLegacyBlockchainApi && blockchainEnabled && issuerOnChain;
  const issuerDid = blockchain?.issuer_did;

  const hasNewBlockchainUi =
    blockchain != null &&
    !showLegacyCombinedUi &&
    (!isLegacyBlockchainApi || blockchainEnabled) &&
    [
      "message",
      "status",
      "issuer_did",
      "vc_anchored_at",
      "credential_hash_full",
      "Network",
      "network",
    ].some((key) => {
      const value = blockchain[key];
      return value != null && String(value).trim() !== "";
    });

  const blockchainIssuerStatus = showLegacyCombinedUi
    ? issuerOnChain
      ? "anchored"
      : "not_anchored"
    : "";
  const blockchainVcStatus = showLegacyCombinedUi ? blockchain?.status : "";
  const blockchainExtraCardCount = showLegacyCombinedUi
    ? 2
    : hasNewBlockchainUi
      ? 1
      : 0;

  useEffect(() => {
    if (!isOpen || !encodedData) return;

    const fetchCredentialDetails = async () => {
      setLoading(true);
      setError("");
      setDetails(null);

      try {
        const response = await GET(
          `${EntityConstants.GET_CREDENTIAL_STATUS_DETAILS}?encoded_data=${encodedData}`,
        );

        const data = await response.json();

        if (!response.ok) {
          const message =
            data?.error ||
            data?.message ||
            data?.detail ||
            t("history:loadError", {
              defaultValue: "Unable to load credential verification details.",
            });

          setError(message);
          Toast({
            type: "error",
            title: t("history:error", { defaultValue: "Error" }),
            message,
          });
          return;
        }

        setDetails(data);
      } catch (err) {
        const message =
          err?.message ||
          t("history:loadError", {
            defaultValue: "Unable to load credential verification details.",
          });

        setError(message);
        Toast({
          type: "error",
          title: t("history:error", { defaultValue: "Error" }),
          message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCredentialDetails();
  }, [encodedData, isOpen, t]);

  useEffect(() => {
    if (!details) return;

    const cardCount = 4 + blockchainExtraCardCount;
    setStepStates(Array.from({ length: cardCount }, () => "pending"));
    setShowMessage(false);
    setShowDetails(false);

    const timers = [];

    for (let i = 0; i < cardCount; i++) {
      timers.push(
        setTimeout(() => {
          setStepStates((prev) =>
            prev.map((s, idx) => (idx === i ? "loading" : s)),
          );
        }, i * STEP_INTERVAL),
      );
      timers.push(
        setTimeout(
          () => {
            setStepStates((prev) =>
              prev.map((s, idx) => (idx === i ? "done" : s)),
            );
          },
          i * STEP_INTERVAL + STEP_LOAD_DURATION,
        ),
      );
    }

    const allDoneAt = (cardCount - 1) * STEP_INTERVAL + STEP_LOAD_DURATION;
    timers.push(setTimeout(() => setShowMessage(true), allDoneAt + 280));
    timers.push(setTimeout(() => setShowDetails(true), allDoneAt + 750));

    return () => timers.forEach(clearTimeout);
  }, [details, blockchainExtraCardCount]);

  useEffect(() => {
    if (!isOpen) {
      setStepStates([]);
      setShowMessage(false);
      setShowDetails(false);
      return;
    }

    if (resolveTimerRef.current) {
      clearTimeout(resolveTimerRef.current);
      resolveTimerRef.current = null;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (resolveTimerRef.current) {
        clearTimeout(resolveTimerRef.current);
      }
    },
    [],
  );

  const inferredStatusKey = inferCredentialStatus(details, fallbackStatus);
  const responseStatusKey = normalizeCredentialStatus(details?.status);
  const statusKey =
    responseStatusKey !== "unknown" ? responseStatusKey : inferredStatusKey;
  const displayStatusLabel =
    details?.status ||
    (statusKey !== "unknown"
      ? toSentenceCase(statusKey.replace(/_/g, " "))
      : t("history:title", { defaultValue: "Credential status" }));
  const useNeutralNotVerifiedPalette =
    responseStatusKey === "not_verified" || statusKey === "not_verified";
  const palette =
    STATUS_TONE_STYLES[
      useNeutralNotVerifiedPalette
        ? "neutral"
        : getCredentialStatusTone(statusKey)
    ];
  const errorPalette = STATUS_TONE_STYLES.danger;
  const OverallIcon = getOverallIcon(
    useNeutralNotVerifiedPalette ? "not_verified" : statusKey,
  );

  const cards = [
    {
      key: "signature",
      section: "signature",
      title: details?.signature?.message,
      subtitle: details?.key_id || null,
      status: details?.signature?.status,
    },
    {
      key: "expiry",
      section: "expiry",
      title: details?.expiry?.message,
      subtitle: details?.expiry?.expired_at
        ? formatDisplayDate(details?.expiry?.expired_at, t)
        : null,
      status: details?.expiry?.status,
    },
    {
      key: "revocation",
      section: "revocation",
      title: details?.revocation?.message,
      subtitle: details?.revocation?.revoked_at
        ? formatDisplayDate(details?.revocation?.revoked_at, t)
        : null,
      status: details?.revocation?.status,
    },
    {
      key: "issuer",
      section: "issuer",
      title: details?.issuer?.message,
      subtitle: details?.issuer_id || null,
      status: details?.issuer?.status,
    },
    ...(showLegacyCombinedUi
      ? [
          {
            key: "issuer_identity",
            section: "issuer_identity",
            render: () => (
              <IssuerIdentityCard
                issuerDid={details?.issuer_id || issuerDid || null}
                issuerResolvedStatus={details?.issuer?.status}
                anchoredStatus={blockchainIssuerStatus}
                showAnchoredPill
                forceNeutral={useNeutralNotVerifiedPalette}
                t={t}
              />
            ),
          },
          {
            key: "blockchain_vc",
            section: "blockchain_vc",
            title: t("history:blockchainVerification", {
              defaultValue: "Blockchain verification",
            }),
            subtitle: (() => {
              const hashMessage =
                blockchain?.message ||
                t("history:credentialHashOnChain", {
                  defaultValue: "Credential hash matches on-chain record",
                });
              const issuerOnChainNote = issuerOnChain
                ? t("history:issuerDidPresentOnBlockchain", {
                    defaultValue: "Issuer DID is present on the blockchain",
                  })
                : t("history:issuerDidNotPresentOnBlockchain", {
                    defaultValue: "Issuer DID is not present on the blockchain",
                  });
              return `${hashMessage}, ${issuerOnChainNote}`;
            })(),
            status: blockchainVcStatus,
          },
        ]
      : []),
    ...(hasNewBlockchainUi
      ? [
          {
            key: "blockchain",
            section: "blockchain",
            render: () => (
              <BlockchainVerificationCard
                blockchain={blockchain}
                forceNeutral={useNeutralNotVerifiedPalette}
                t={t}
              />
            ),
          },
        ]
      : []),
  ];

  const handleClose = () => {
    onClose();
    if (details && onResolved) {
      const statusLabel = displayStatusLabel;
      if (resolveTimerRef.current) {
        clearTimeout(resolveTimerRef.current);
      }
      resolveTimerRef.current = setTimeout(() => {
        onResolved(statusLabel);
        resolveTimerRef.current = null;
      }, 300);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/45 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="credential-status-details-modal"
                className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl"
              >
                <Dialog.Title className="sr-only">
                  {displayStatusLabel}
                </Dialog.Title>
                <button
                  type="button"
                  id="credential-status-details-modal-close"
                  className="absolute right-4 top-4 rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
                  onClick={handleClose}
                  aria-label={t("history:closeModal", {
                    defaultValue: "Close modal",
                  })}
                >
                  <XIcon size={20} className="inline-flex" aria-hidden />
                </button>

                <div className="max-h-[85vh] overflow-y-auto bg-[linear-gradient(180deg,#ffffff_0%,#fbfcfe_100%)] px-4 py-4 sm:px-6 sm:py-6">
                  {loading ? (
                    <LoadingSkeleton />
                  ) : error ? (
                    <div className="flex min-h-[18rem] items-center justify-center">
                      <div className="max-w-md text-center">
                        <div
                          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border"
                          style={{
                            backgroundColor: errorPalette.surface,
                            borderColor: errorPalette.border,
                            color: errorPalette.text,
                          }}
                        >
                          <AlertCircle className="h-8 w-8" />
                        </div>
                        <p className="mt-4 text-xl font-semibold text-[#111827]">
                          {t("history:unavailableVerification", {
                            defaultValue: "Verification details unavailable",
                          })}
                        </p>
                        <p className="mt-2 text-sm text-gray-600">{error}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex flex-col items-center text-center">
                        <div
                          className="flex h-14 w-14 items-center justify-center rounded-full border-2"
                          style={{
                            backgroundColor: palette.surfaceMuted,
                            borderColor: palette.border,
                            color: palette.text,
                          }}
                        >
                          <OverallIcon className="h-6 w-6" />
                        </div>

                        <span
                          className="mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold sm:mt-4 sm:px-4 sm:py-1.5 sm:text-sm"
                          style={{
                            backgroundColor: palette.surface,
                            borderColor: palette.border,
                            color: palette.text,
                          }}
                        >
                          <span className="h-2 w-2 rounded-full bg-current" />
                          {displayStatusLabel}
                        </span>
                      </div>

                      <div className="grid gap-3">
                        {cards.map((card, index) => {
                          const state = stepStates[index];
                          return (
                            <div key={card.key}>
                              {state === "pending" && <StepSkeletonCard />}
                              {state === "loading" && <StepLoadingCard />}
                              {state === "done" && (
                                <Transition
                                  as={Fragment}
                                  appear
                                  show
                                  enter="transform transition-[transform,opacity] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                                  enterFrom="translate-x-6 opacity-0 scale-[0.97]"
                                  enterTo="translate-x-0 opacity-100 scale-100"
                                >
                                  <div>
                                    {typeof card.render === "function" ? (
                                      card.render()
                                    ) : (
                                      <VerificationCheckCard
                                        section={card.section}
                                        title={card.title}
                                        subtitle={card.subtitle}
                                        status={card.status}
                                        forceNeutral={
                                          useNeutralNotVerifiedPalette
                                        }
                                        t={t}
                                      />
                                    )}
                                  </div>
                                </Transition>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {details?.message && (
                        <div>
                          {!showMessage ? (
                            <div className="rounded-2xl border-l-4 border-gray-100 bg-gray-50 px-3 py-4">
                              <ShimmerBlock className="h-4 w-full" />
                              <ShimmerBlock className="mt-2 h-4 w-3/4" />
                            </div>
                          ) : (
                            <Transition
                              as={Fragment}
                              appear
                              show
                              enter="transform transition-[transform,opacity] duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                              enterFrom="translate-y-2 opacity-0"
                              enterTo="translate-y-0 opacity-100"
                            >
                              <div
                                className="rounded-2xl border-l-4 px-3 py-3"
                                style={{
                                  backgroundColor: palette.surfaceMuted,
                                  borderLeftColor: palette.border,
                                }}
                              >
                                <p className="text-sm leading-6 text-gray-600">
                                  {details?.message}
                                </p>
                              </div>
                            </Transition>
                          )}
                        </div>
                      )}

                      <div className="border-t border-gray-200 pt-4">
                        {!showDetails ? (
                          <div className="grid gap-x-6 gap-y-3 md:grid-cols-[140px_1fr]">
                            {[0, 1, 2, 3].map((i) => (
                              <Fragment key={i}>
                                <ShimmerBlock className="h-4 w-20" />
                                <ShimmerBlock className="ml-auto h-4 w-40" />
                              </Fragment>
                            ))}
                          </div>
                        ) : (
                          <Transition
                            as={Fragment}
                            appear
                            show
                            enter="transform transition-[transform,opacity] duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                            enterFrom="translate-y-2 opacity-0"
                            enterTo="translate-y-0 opacity-100"
                          >
                            <div className="grid gap-x-6 gap-y-3 md:grid-cols-[140px_1fr]">
                              {DETAILS_LAYOUT.map((item) => {
                                const rawValue = item.path
                                  ? getValueAtPath(details, item.path)
                                  : details?.[item.key];

                                if (!rawValue) return null;

                                const pathKey = item.path?.join(".");
                                const isMono =
                                  item.key === "issuer_id" ||
                                  item.key === "key_id" ||
                                  item.key === "subject_id";

                                return (
                                  <Fragment key={item.label}>
                                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
                                      {t(item.label, {
                                        defaultValue: item.label,
                                      })}
                                    </div>
                                    <div
                                      className={`break-words text-right text-sm text-[#111827] ${
                                        isMono ? "font-mono" : ""
                                      }`}
                                      style={{
                                        color:
                                          pathKey === "expiry.expired_at" &&
                                          statusKey === "expired"
                                            ? palette.text
                                            : pathKey ===
                                                  "revocation.revoked_at" &&
                                                statusKey === "revoked"
                                              ? palette.text
                                              : undefined,
                                      }}
                                    >
                                      {item.isDate
                                        ? formatDisplayDate(rawValue, t)
                                        : isMono
                                          ? truncateMiddle(rawValue, t)
                                          : rawValue}
                                    </div>
                                  </Fragment>
                                );
                              })}
                            </div>
                          </Transition>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
